import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

export default function SeriesDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    curriculum_box: "",
    esa_alignment: "",
    location: "Graceful Garden Homestead, Chino Valley, AZ",
    event_address: "",
    what_to_bring: "",
    price_student: 55,
    price_parent: 12,
    capacity_total: 20,
    start_date: "",
    start_time: "09:00",
    end_time: "12:00",
    cadence: "weekly",
    occurrences: 4,
    status: "open",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title || !form.start_date || !form.description || !form.curriculum_box || !form.esa_alignment) {
      toast.error("Title, description, curriculum box, ESA alignment, and start date are required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/recurring-series", {
        ...form,
        price_student: Number(form.price_student),
        price_parent: Number(form.price_parent),
        capacity_total: Number(form.capacity_total),
        occurrences: Number(form.occurrences),
      });
      toast.success(`Created ${data.count} classes`);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Series creation failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="series-dialog">
        <DialogHeader>
          <DialogTitle>Create recurring class series</DialogTitle>
          <p className="text-xs text-gg-muted">
            Spawns N child classes from this template (titles auto-append the date for each occurrence).
          </p>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 pt-2">
          <Field label="Series title" className="sm:col-span-2">
            <Input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Saturday Garden Series"
              data-testid="series-title-input"
            />
          </Field>
          <Field label="First class date">
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => update("start_date", e.target.value)}
              data-testid="series-start-date-input"
            />
          </Field>
          <Field label="Cadence">
            <select
              value={form.cadence}
              onChange={(e) => update("cadence", e.target.value)}
              className="w-full border border-gg-border rounded-md px-3 py-2 text-sm bg-white"
              data-testid="series-cadence-select"
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
            </select>
          </Field>
          <Field label="Start time">
            <Input
              type="time"
              value={form.start_time}
              onChange={(e) => update("start_time", e.target.value)}
              data-testid="series-start-time-input"
            />
          </Field>
          <Field label="End time">
            <Input
              type="time"
              value={form.end_time}
              onChange={(e) => update("end_time", e.target.value)}
              data-testid="series-end-time-input"
            />
          </Field>
          <Field label="Number of classes">
            <Input
              type="number"
              min="1"
              max="52"
              value={form.occurrences}
              onChange={(e) => update("occurrences", e.target.value)}
              data-testid="series-occurrences-input"
            />
          </Field>
          <Field label="Capacity (per class)">
            <Input
              type="number"
              min="1"
              value={form.capacity_total}
              onChange={(e) => update("capacity_total", e.target.value)}
            />
          </Field>
          <Field label="Student price ($)">
            <Input
              type="number"
              step="0.01"
              value={form.price_student}
              onChange={(e) => update("price_student", e.target.value)}
            />
          </Field>
          <Field label="Parent add-on price ($)">
            <Input
              type="number"
              step="0.01"
              value={form.price_parent}
              onChange={(e) => update("price_parent", e.target.value)}
            />
          </Field>
          <Field label="Location" className="sm:col-span-2">
            <Input
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
            />
          </Field>
          <Field label="Event venue address (private)" className="sm:col-span-2">
            <Input
              value={form.event_address}
              onChange={(e) => update("event_address", e.target.value)}
              placeholder="Shared in confirmation emails. Leave blank to share later."
            />
          </Field>
          <Field label="Description" className="sm:col-span-2">
            <Textarea
              rows={3}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
            />
          </Field>
          <Field label="Curriculum box (shown on invoice)" className="sm:col-span-2">
            <Textarea
              rows={2}
              value={form.curriculum_box}
              onChange={(e) => update("curriculum_box", e.target.value)}
            />
          </Field>
          <Field label="ESA alignment (PDF only — hidden from public)" className="sm:col-span-2">
            <Textarea
              rows={2}
              value={form.esa_alignment}
              onChange={(e) => update("esa_alignment", e.target.value)}
            />
          </Field>
          <Field label="What to bring / wear" className="sm:col-span-2">
            <Textarea
              rows={2}
              value={form.what_to_bring}
              onChange={(e) => update("what_to_bring", e.target.value)}
            />
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn-pill btn-outline"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="btn-pill btn-primary"
            disabled={saving}
            data-testid="series-submit-btn"
          >
            {saving ? "Creating…" : `Create ${form.occurrences || 0} classes`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block text-sm space-y-1 ${className}`}>
      <span className="text-xs uppercase tracking-[0.14em] text-gg-brown font-semibold">{label}</span>
      {children}
    </label>
  );
}
